import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import {
  Bell, UserPlus, ShoppingBag, Eye, MousePointerClick,
  Volume2, VolumeX, CheckCheck, Inbox, MessageSquare,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const adminHeaders = () => ({ 'x-admin-token': sessionStorage.getItem('ml_admin_token') || '' });

const POLL_MS = 15000;
const SOUND_KEY = 'ml_notif_sound';

/* Per-event visual treatment (dark admin theme). */
const EVENT_META = {
  new_lead:      { label: 'New Lead', icon: UserPlus,          cls: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30' },
  new_chat_lead: { label: 'Chat Lead', icon: MessageSquare,    cls: 'bg-cyan-500/15 text-cyan-300 border-cyan-500/30' },
  new_order:     { label: 'Order',    icon: ShoppingBag,       cls: 'bg-brand-blue/15 text-brand-blue border-brand-blue/30' },
  email_opened:  { label: 'Opened',   icon: Eye,               cls: 'bg-amber-500/15 text-amber-400 border-amber-500/30' },
  email_clicked: { label: 'Clicked',  icon: MousePointerClick, cls: 'bg-purple-500/15 text-purple-300 border-purple-500/30' },
};
const DEFAULT_META = { label: 'Activity', icon: Bell, cls: 'bg-slate-500/15 text-slate-300 border-slate-500/30' };

function timeAgo(iso) {
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (isNaN(s)) return '';
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60); if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`;
  const dd = Math.floor(h / 24); if (dd < 7) return `${dd}d ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/* Quiet two-note Web Audio chime — no asset needed. Safe if autoplay blocked. */
let _audioCtx = null;
function playChime() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    _audioCtx = _audioCtx || new Ctx();
    const ctx = _audioCtx;
    if (ctx.state === 'suspended') ctx.resume();
    const now = ctx.currentTime;
    const note = (freq, start, dur, peak) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = 'sine';
      o.frequency.value = freq;
      g.gain.setValueAtTime(0.0001, now + start);
      g.gain.exponentialRampToValueAtTime(peak, now + start + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, now + start + dur);
      o.connect(g); g.connect(ctx.destination);
      o.start(now + start); o.stop(now + start + dur + 0.02);
    };
    note(880, 0, 0.32, 0.06);       // A5
    note(1174.66, 0.12, 0.34, 0.05); // D6
  } catch (e) { /* ignore audio errors */ }
}

export const NotificationCenter = () => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [soundEnabled, setSoundEnabled] = useState(() => localStorage.getItem(SOUND_KEY) === 'true');

  const seen = useRef(new Set());
  const initialized = useRef(false);
  const soundRef = useRef(soundEnabled);
  useEffect(() => { soundRef.current = soundEnabled; }, [soundEnabled]);

  const poll = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/notifications?limit=20`, { headers: adminHeaders() });
      const data = res.data || {};
      const list = Array.isArray(data.notifications) ? data.notifications : [];
      setItems(list);
      setUnread(data.unread_count || 0);

      // New-arrival detection (skip the very first fetch to avoid a burst of toasts).
      if (initialized.current) {
        const fresh = list.filter((n) => !seen.current.has(n.id) && !n.is_read);
        if (fresh.length === 1) {
          toast(fresh[0].title, { description: fresh[0].message, position: 'top-right' });
          if (soundRef.current) playChime();
        } else if (fresh.length > 1) {
          toast(`${fresh.length} new notifications`, { description: 'Open the bell to review the latest activity.', position: 'top-right' });
          if (soundRef.current) playChime();
        }
      } else {
        initialized.current = true;
      }
      list.forEach((n) => seen.current.add(n.id));
    } catch (e) {
      // Silent: likely a transient error or 401 handled by the dashboard shell.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    poll();
    const iv = setInterval(poll, POLL_MS);
    return () => clearInterval(iv);
  }, [poll]);

  const toggleSound = useCallback(() => {
    setSoundEnabled((v) => {
      const nv = !v;
      localStorage.setItem(SOUND_KEY, nv ? 'true' : 'false');
      if (nv) playChime(); // audible confirmation + unlocks the AudioContext via user gesture
      return nv;
    });
  }, []);

  const markOne = useCallback(async (n) => {
    if (n.is_read) return;
    setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, is_read: true } : x)));
    setUnread((u) => Math.max(0, u - 1));
    try {
      await axios.post(`${API}/admin/notifications/${n.id}/read`, {}, { headers: adminHeaders() });
    } catch (e) { /* optimistic; next poll reconciles */ }
  }, []);

  const markAll = useCallback(async () => {
    if (unread === 0) return;
    setItems((prev) => prev.map((x) => ({ ...x, is_read: true })));
    setUnread(0);
    try {
      await axios.post(`${API}/admin/notifications/read-all`, {}, { headers: adminHeaders() });
    } catch (e) { /* optimistic */ }
  }, [unread]);

  return (
    <>
      {/* Bell trigger */}
      <button
        onClick={() => setOpen(true)}
        className="relative w-10 h-10 rounded-lg border border-slate-700 bg-slate-800 text-slate-300 hover:text-white hover:bg-slate-700 hover:border-slate-600 transition-colors flex items-center justify-center focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-brand-blue/25"
        aria-label={`Notifications${unread ? ` (${unread} unread)` : ''}`}
        data-testid="notification-bell"
      >
        <Bell className="w-5 h-5" />
        {unread > 0 && (
          <span
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center ring-2 ring-slate-900"
            data-testid="notification-badge"
          >
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          side="right"
          className="bg-slate-900 border-l border-slate-700 text-white w-full sm:max-w-md p-0 flex flex-col [&>button]:text-slate-400 [&>button]:hover:text-white"
          data-testid="notification-drawer"
        >
          <SheetHeader className="p-4 border-b border-slate-700 space-y-0 text-left">
            <SheetTitle className="text-white text-base font-bold flex items-center gap-2">
              <Bell className="w-4 h-4 text-brand-blue" /> Notifications
              {unread > 0 && (
                <span className="ml-1 min-w-[20px] h-5 px-1.5 rounded-full bg-brand-blue text-white text-[11px] font-bold flex items-center justify-center">
                  {unread}
                </span>
              )}
            </SheetTitle>
            <SheetDescription className="sr-only">Recent activity: new leads, orders and email engagement.</SheetDescription>
          </SheetHeader>

          {/* Sub-header: sound toggle + mark all */}
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-800 bg-slate-900/60">
            <button
              onClick={toggleSound}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-400 hover:text-white transition-colors"
              aria-pressed={soundEnabled}
              title={soundEnabled ? 'Sound on — click to mute' : 'Sound off — click to enable chime'}
              data-testid="notif-sound-toggle"
            >
              {soundEnabled ? <Volume2 className="w-4 h-4 text-brand-blue" /> : <VolumeX className="w-4 h-4" />}
              {soundEnabled ? 'Sound on' : 'Sound off'}
            </button>
            <button
              onClick={markAll}
              disabled={unread === 0}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-blue hover:text-brand-blue/80 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              data-testid="notif-mark-all"
            >
              <CheckCheck className="w-3.5 h-3.5" /> Mark all as read
            </button>
          </div>

          {/* Stream */}
          <div className="flex-1 overflow-y-auto" data-testid="notif-list">
            {loading ? (
              <div className="p-4 space-y-3" data-testid="notif-loading">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="flex gap-3">
                    <div className="w-8 h-8 rounded-lg bg-slate-800 animate-pulse shrink-0" />
                    <div className="flex-1 space-y-2 py-0.5">
                      <div className="h-3 w-3/4 bg-slate-800 rounded animate-pulse" />
                      <div className="h-2.5 w-1/2 bg-slate-800 rounded animate-pulse" />
                    </div>
                  </div>
                ))}
              </div>
            ) : items.length === 0 ? (
              <div className="flex flex-col items-center justify-center text-center py-20 px-6" data-testid="notif-empty">
                <div className="w-12 h-12 rounded-2xl bg-slate-800 flex items-center justify-center mb-3">
                  <Inbox className="w-6 h-6 text-slate-500" />
                </div>
                <p className="text-sm font-semibold text-white">You're all caught up</p>
                <p className="text-xs text-slate-500 mt-1">New leads, orders and email activity will appear here.</p>
              </div>
            ) : (
              items.map((n) => {
                const meta = EVENT_META[n.event_type] || DEFAULT_META;
                const Icon = meta.icon;
                return (
                  <button
                    key={n.id}
                    onClick={() => markOne(n)}
                    className={`w-full text-left flex gap-3 px-4 py-3 border-b border-slate-800 hover:bg-slate-800/60 transition-colors ${!n.is_read ? 'bg-slate-800/30' : ''}`}
                    data-testid={`notif-item-${n.id}`}
                  >
                    <span className={`mt-0.5 w-8 h-8 rounded-lg border flex items-center justify-center shrink-0 ${meta.cls}`}>
                      <Icon className="w-4 h-4" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold text-white truncate flex-1">{n.title}</p>
                        {!n.is_read && <span className="w-2 h-2 rounded-full bg-brand-blue shrink-0" aria-label="unread" />}
                      </div>
                      <p className="text-xs text-slate-400 truncate">{n.message}</p>
                      <p className="text-[11px] text-slate-500 mt-0.5">{timeAgo(n.created_at)}</p>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};
